import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';

interface CustomOrder {
  _id: string;
  user: { name: string; phone: string };
  customOrderDescription: string;
  totalAmount: number;
  deliveryCharge: number;
  status: string;
  paymentMethod: string;
  assignedPartner?: { name: string; phone: string };
  directPaymentRequest?: {
    qrCodeImage?: string;
    storePhone?: string;
    storeName?: string;
    amount?: number;
    customerReceipt?: string;
    partnerNotes?: string;
    sentAt?: string;
    receiptSentAt?: string;
  };
  deliveryAddress: string;
  createdAt: string;
}

interface CustomOrdersManagementProps {
  onRefresh: () => void;
}

const CustomOrdersManagement: React.FC<CustomOrdersManagementProps> = ({ onRefresh }) => {
  const [customOrders, setCustomOrders] = useState<CustomOrder[]>([]);
  const [filter, setFilter] = useState<string>('All');
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<CustomOrder | null>(null);

  useEffect(() => {
    loadCustomOrders();
  }, []);

  const loadCustomOrders = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getAllOrders();
      // Filter only custom orders (isDirectCustomOrder = true)
      const customOnly = response.data.filter((order: any) => order.isDirectCustomOrder === true);
      setCustomOrders(customOnly);
    } catch (error) {
      console.error('Error loading custom orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id: string, newStatus: string) => {
    try {
      await adminAPI.updateOrderStatus(id, newStatus);
      alert('Order status updated');
      loadCustomOrders();
      onRefresh();
    } catch (error) {
      alert('Failed to update status');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this custom order?')) {
      try {
        await adminAPI.deleteOrder(id);
        alert('Custom order deleted successfully');
        loadCustomOrders();
        onRefresh();
      } catch (error) {
        alert('Failed to delete order');
      }
    }
  };

  const getFilteredOrders = () => {
    if (filter === 'All') return customOrders;
    if (filter === 'Pending Payment') {
      return customOrders.filter(o => 
        ['Partner Assigned', 'Waiting for Payment'].includes(o.status)
      );
    }
    if (filter === 'Payment Received') {
      return customOrders.filter(o => 
        ['Payment Received', 'Purchasing Items'].includes(o.status)
      );
    }
    if (filter === 'In Progress') {
      return customOrders.filter(o => 
        ['Partner En Route to Store', 'At Store', 'Items Packed', 'Out for Delivery', 'At Customer Location'].includes(o.status)
      );
    }
    return customOrders.filter(o => o.status === filter);
  };

  const filteredOrders = getFilteredOrders();

  const getStatusClass = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'Pending': 'status-pending',
      'Partner Assigned': 'status-confirmed',
      'Waiting for Payment': 'status-preparing',
      'Payment Received': 'status-confirmed',
      'Purchasing Items': 'status-preparing',
      'Partner En Route to Store': 'status-preparing',
      'At Store': 'status-preparing',
      'Items Packed': 'status-preparing',
      'Out for Delivery': 'status-delivering',
      'At Customer Location': 'status-delivering',
      'Delivered': 'status-delivered',
      'Cancelled': 'status-cancelled',
    };
    return statusMap[status] || 'status-pending';
  };

  const getStatusIcon = (status: string) => {
    const iconMap: { [key: string]: string } = {
      'Pending': '⏳',
      'Partner Assigned': '🚴',
      'Waiting for Payment': '💳',
      'Payment Received': '✅',
      'Purchasing Items': '🛒',
      'Partner En Route to Store': '🏃',
      'At Store': '🏪',
      'Items Packed': '📦',
      'Out for Delivery': '🚚',
      'At Customer Location': '📍',
      'Delivered': '✅',
      'Cancelled': '❌',
    };
    return iconMap[status] || '📦';
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">📱 Custom Orders (Order Anything)</h2>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {['All', 'Pending', 'Pending Payment', 'Payment Received', 'In Progress', 'Delivered', 'Cancelled'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              style={{
                padding: '10px 15px',
                background: filter === status ? '#fc8019' : '#f5f5f5',
                color: filter === status ? 'white' : '#333',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: filter === status ? 'bold' : 'normal',
                fontSize: '13px',
              }}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Summary */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
        gap: '15px', 
        marginBottom: '20px' 
      }}>
        <div style={{ background: '#fff3e0', padding: '15px', borderRadius: '10px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fc8019' }}>
            {customOrders.length}
          </div>
          <div style={{ color: '#666', fontSize: '12px' }}>Total Custom Orders</div>
        </div>
        <div style={{ background: '#e3f2fd', padding: '15px', borderRadius: '10px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1976d2' }}>
            {customOrders.filter(o => ['Partner Assigned', 'Waiting for Payment'].includes(o.status)).length}
          </div>
          <div style={{ color: '#666', fontSize: '12px' }}>Awaiting Payment</div>
        </div>
        <div style={{ background: '#e8f5e9', padding: '15px', borderRadius: '10px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#388e3c' }}>
            {customOrders.filter(o => o.status === 'Delivered').length}
          </div>
          <div style={{ color: '#666', fontSize: '12px' }}>Delivered</div>
        </div>
        <div style={{ background: '#fce4ec', padding: '15px', borderRadius: '10px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#c62828' }}>
            ₹{customOrders.filter(o => o.status === 'Delivered').reduce((sum, o) => sum + (o.totalAmount || 0), 0)}
          </div>
          <div style={{ color: '#666', fontSize: '12px' }}>Total Revenue</div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading custom orders...</div>
      ) : filteredOrders.length === 0 ? (
        <div className="empty-state">
          <h3>No Custom Orders Found</h3>
          <p>No custom orders match the selected filter.</p>
        </div>
      ) : (
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Partner</th>
                <th>Payment Info</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order._id}>
                  <td>
                    <strong>#{order._id.slice(-6)}</strong>
                    <br/>
                    <small style={{ color: '#fc8019' }}>Custom</small>
                  </td>
                  <td>
                    {order.user?.name || 'N/A'}<br/>
                    <small style={{ color: '#666' }}>{order.user?.phone || ''}</small>
                  </td>
                  <td style={{ maxWidth: '200px' }}>
                    <div style={{ 
                      whiteSpace: 'nowrap', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis',
                      cursor: 'pointer'
                    }} title={order.customOrderDescription}>
                      {order.customOrderDescription || 'No description'}
                    </div>
                  </td>
                  <td>
                    <strong>₹{order.totalAmount?.toFixed(2) || '0.00'}</strong>
                    <br/>
                    <small style={{ color: '#666' }}>
                      (Delivery: ₹{order.deliveryCharge || 60})
                    </small>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusClass(order.status)}`}>
                      {getStatusIcon(order.status)} {order.status}
                    </span>
                  </td>
                  <td>
                    {order.assignedPartner ? (
                      <>
                        {order.assignedPartner.name}<br/>
                        <small style={{ color: '#666' }}>{order.assignedPartner.phone || ''}</small>
                      </>
                    ) : (
                      <span style={{ color: '#999' }}>Unassigned</span>
                    )}
                  </td>
                  <td>
                    {order.directPaymentRequest ? (
                      <div style={{ fontSize: '12px' }}>
                        {order.directPaymentRequest.storeName && (
                          <div>🏪 {order.directPaymentRequest.storeName}</div>
                        )}
                        {order.directPaymentRequest.amount && (
                          <div>💰 ₹{order.directPaymentRequest.amount}</div>
                        )}
                        {order.directPaymentRequest.customerReceipt && (
                          <div style={{ color: '#28a745' }}>✅ Receipt Sent</div>
                        )}
                        <button
                          onClick={() => setSelectedOrder(order)}
                          style={{
                            background: '#e3f2fd',
                            border: 'none',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '11px',
                            marginTop: '5px'
                          }}
                        >
                          View Details
                        </button>
                      </div>
                    ) : (
                      <span style={{ color: '#999', fontSize: '12px' }}>No payment info</span>
                    )}
                  </td>
                  <td style={{ fontSize: '12px' }}>
                    {new Date(order.createdAt).toLocaleDateString()}<br/>
                    <small>{new Date(order.createdAt).toLocaleTimeString()}</small>
                  </td>
                  <td>
                    {order.status !== 'Delivered' && order.status !== 'Cancelled' && (
                      <select
                        onChange={(e) => handleStatusUpdate(order._id, e.target.value)}
                        value=""
                        style={{ padding: '5px', marginBottom: '5px', width: '100%', fontSize: '12px' }}
                      >
                        <option value="" disabled>Change Status</option>
                        <option value="Partner Assigned">Partner Assigned</option>
                        <option value="Waiting for Payment">Waiting for Payment</option>
                        <option value="Payment Received">Payment Received</option>
                        <option value="Purchasing Items">Purchasing Items</option>
                        <option value="Out for Delivery">Out for Delivery</option>
                        <option value="Delivered">Delivered</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    )}
                    <button 
                      className="action-btn delete-btn" 
                      onClick={() => handleDelete(order._id)}
                      style={{ width: '100%', fontSize: '12px' }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Payment Details Modal */}
      {selectedOrder && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '15px',
            padding: '25px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>📱 Custom Order Details</h3>
              <button 
                onClick={() => setSelectedOrder(null)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '24px', 
                  cursor: 'pointer' 
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong>Order ID:</strong> #{selectedOrder._id.slice(-6)}
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong>Description:</strong>
              <div style={{ 
                background: '#f5f5f5', 
                padding: '10px', 
                borderRadius: '8px', 
                marginTop: '5px' 
              }}>
                {selectedOrder.customOrderDescription || 'No description'}
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong>Customer:</strong> {selectedOrder.user?.name} ({selectedOrder.user?.phone})
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong>Partner:</strong> {selectedOrder.assignedPartner?.name || 'Unassigned'}
              {selectedOrder.assignedPartner?.phone && ` (${selectedOrder.assignedPartner.phone})`}
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong>Delivery Address:</strong>
              <div style={{ color: '#666', fontSize: '14px' }}>
                {selectedOrder.deliveryAddress || 'N/A'}
              </div>
            </div>

            {selectedOrder.directPaymentRequest && (
              <>
                <hr style={{ margin: '20px 0', border: 'none', borderTop: '1px solid #eee' }} />
                <h4 style={{ color: '#fc8019' }}>💳 Payment Request Details</h4>

                {selectedOrder.directPaymentRequest.storeName && (
                  <div style={{ marginBottom: '10px' }}>
                    <strong>Store Name:</strong> {selectedOrder.directPaymentRequest.storeName}
                  </div>
                )}

                {selectedOrder.directPaymentRequest.storePhone && (
                  <div style={{ marginBottom: '10px' }}>
                    <strong>Store Phone:</strong> {selectedOrder.directPaymentRequest.storePhone}
                  </div>
                )}

                {selectedOrder.directPaymentRequest.amount && (
                  <div style={{ marginBottom: '10px' }}>
                    <strong>Requested Amount:</strong> ₹{selectedOrder.directPaymentRequest.amount}
                  </div>
                )}

                {selectedOrder.directPaymentRequest.partnerNotes && (
                  <div style={{ marginBottom: '10px' }}>
                    <strong>Partner Notes:</strong>
                    <div style={{ 
                      background: '#fff3e0', 
                      padding: '10px', 
                      borderRadius: '8px',
                      marginTop: '5px'
                    }}>
                      {selectedOrder.directPaymentRequest.partnerNotes}
                    </div>
                  </div>
                )}

                {selectedOrder.directPaymentRequest.qrCodeImage && (
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Store QR Code:</strong>
                    <div style={{ marginTop: '10px' }}>
                      <img 
                        src={selectedOrder.directPaymentRequest.qrCodeImage} 
                        alt="QR Code" 
                        style={{ 
                          maxWidth: '200px', 
                          borderRadius: '8px',
                          border: '1px solid #ddd'
                        }} 
                      />
                    </div>
                  </div>
                )}

                {selectedOrder.directPaymentRequest.customerReceipt && (
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Customer Payment Receipt:</strong>
                    <div style={{ marginTop: '10px' }}>
                      <img 
                        src={selectedOrder.directPaymentRequest.customerReceipt} 
                        alt="Payment Receipt" 
                        style={{ 
                          maxWidth: '200px', 
                          borderRadius: '8px',
                          border: '2px solid #28a745'
                        }} 
                      />
                    </div>
                  </div>
                )}

                {selectedOrder.directPaymentRequest.sentAt && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    Payment request sent: {new Date(selectedOrder.directPaymentRequest.sentAt).toLocaleString()}
                  </div>
                )}

                {selectedOrder.directPaymentRequest.receiptSentAt && (
                  <div style={{ fontSize: '12px', color: '#28a745' }}>
                    Receipt sent: {new Date(selectedOrder.directPaymentRequest.receiptSentAt).toLocaleString()}
                  </div>
                )}
              </>
            )}

            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button
                onClick={() => setSelectedOrder(null)}
                style={{
                  background: '#fc8019',
                  color: 'white',
                  border: 'none',
                  padding: '10px 25px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomOrdersManagement;
